<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :actions="actions"
      :title="title"
      :beforeSetupGrid="beforeSetupGrid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>

    <!-- Process Request Dialog -->
    <v-dialog persistent v-model="processDialog" max-width="1100">
      <v-card class="request-dialog">
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-message-alert</v-icon
          >
          <span class="dialog-title">Process Bus Change Request</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text my-0" icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3">
          <!-- Three Column Layout -->
          <v-layout row wrap>
            <!-- Left Column: Student Info -->
            <v-flex xs12 md3 class="pr-2">
              <div class="section-header mb-3">
                <v-icon x-small class="mr-1">mdi-account</v-icon>
                Student Info
              </div>
              <v-card outlined class="pa-2 original-request-card mb-2">
                <div class="recording-header">
                  <v-icon small color="#ff6b6b" class="recording-icon"
                    >mdi-microphone</v-icon
                  >
                  <span class="recording-label">Original Request</span>
                </div>
                <audio
                  v-if="getRecordingUrl"
                  ref="audioPlayer"
                  controls
                  :src="getRecordingUrl"
                  class="inline-audio-player mt-1"
                ></audio>
                <div v-else class="caption grey--text mt-1">No message</div>
              </v-card>
              <v-card outlined class="pa-2 compact-info" v-if="selectedRequest">
                <div class="info-row">
                  <span class="info-label">Name:</span>
                  <span class="info-value">{{
                    selectedRequest.studentName
                  }}</span>
                </div>
                <div class="info-row" v-if="selectedRequest.studentNameHeb">
                  <span class="info-label">Hebrew:</span>
                  <span class="info-value">{{
                    selectedRequest.studentNameHeb
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Student ID:</span>
                  <span class="info-value">{{
                    selectedRequest.studentID
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Source:</span>
                  <span class="info-value">{{
                    selectedRequest.source || selectedRequest.Source || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Caller:</span>
                  <span class="info-value">{{
                    formatPhone(selectedRequest.callerID || selectedRequest.CallerID) ||
                      "Unknown"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Requested:</span>
                  <span class="info-value">{{
                    formatDate(selectedRequest.date || selectedRequest.Date) || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Building/Grade/Section:</span>
                </div>
                <div class="info-row">
                  <span class="info-value info-value-full">
                    {{ selectedRequest.building }} -
                    {{ selectedRequest.class }} -
                    {{ selectedRequest.section }}
                  </span>
                </div>
                <div
                  class="info-row"
                  v-if="selectedRequest.teacherH || selectedRequest.teacherH2"
                >
                  <span class="info-label">Teacher:</span>
                  <span class="info-value">{{
                    getTeacherDisplay(selectedRequest)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Home:</span>
                  <span class="info-value">{{
                    selectedRequest.homePhone || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Cell:</span>
                  <span class="info-value">{{
                    selectedRequest.cellPhone || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Bus Addr:</span>
                  <span class="info-value">{{
                    selectedRequest.busAddress1 || "-"
                  }}</span>
                </div>
                <div
                  class="info-row"
                  v-if="selectedRequest.notificationPhoneNumber"
                >
                  <span class="info-label">Notify:</span>
                  <span class="info-value">
                    {{ formatPhone(selectedRequest.notificationPhoneNumber) }}
                    <span
                      class="grey--text"
                      v-if="selectedRequest.notificationType"
                    >
                      ({{ selectedRequest.notificationType }})
                    </span>
                  </span>
                </div>
              </v-card>
            </v-flex>

            <!-- Middle Column: Bus Change Form -->
            <v-flex xs12 md6 class="px-2">
              <div class="section-header mb-3">
                <v-icon x-small class="mr-1">mdi-bus-marker</v-icon>
                Bus Change Details
              </div>

              <v-layout row wrap>
                <v-flex xs6 class="pr-1">
                  <v-card outlined class="pa-2 period-section-card mb-2">
                    <div class="caption period-title mb-1">Starting Run</div>
                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="formData.startDate"
                          label="Start Date *"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          class="mb-2"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="formData.startDate"
                        color="rgb(92, 184, 96)"
                        @input="startDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                    <v-select
                      v-model="formData.startPeriod"
                      :items="periodOptions"
                      item-text="label"
                      item-value="value"
                      label="Run *"
                      prepend-inner-icon="mdi-weather-sunset-up"
                      outlined
                      dense
                      hide-details
                      :rules="[rules.required]"
                    ></v-select>
                  </v-card>
                </v-flex>
                <v-flex xs6 class="pl-1">
                  <v-card outlined class="pa-2 period-section-card mb-2">
                    <div class="caption period-title mb-1">Ending After</div>
                    <v-menu
                      v-model="endDateMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="formData.endDate"
                          label="End Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          class="mb-2"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="formData.endDate"
                        color="rgb(92, 184, 96)"
                        :min="formData.startDate"
                        @input="endDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                    <v-select
                      v-model="formData.endPeriod"
                      :items="periodOptions"
                      item-text="label"
                      item-value="value"
                      label="Run *"
                      prepend-inner-icon="mdi-weather-sunset-down"
                      outlined
                      dense
                      hide-details
                      :rules="[rules.required]"
                    ></v-select>
                  </v-card>
                </v-flex>
              </v-layout>
              <div
                class="caption grey--text text--darken-1 mb-3 period-helper-text"
              >
                Morning Pickup: Includes the trip TO school. Afternoon Dropoff:
                Includes the trip FROM school.
              </div>

              <v-alert
                v-if="deadlineWarningMessage"
                type="warning"
                outline
                dense
                value="true"
                class="mb-3 pa-2"
              >
                {{ deadlineWarningMessage }}
              </v-alert>

              <v-text-field
                v-model="formData.busChangeAddress"
                label="Bus Change Address *"
                prepend-inner-icon="mdi-map-marker"
                outlined
                dense
                hide-details
                class="mb-3"
                autocomplete="off"
              ></v-text-field>

              <v-layout row wrap class="mb-3">
                <v-flex xs12>
                  <v-text-field
                    v-model="formData.busChangeAccountID"
                    label="Account ID"
                    prepend-inner-icon="mdi-account-search"
                    outlined
                    dense
                    hide-details
                    readonly
                    @click="openAccountSearch"
                    style="cursor: pointer;"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap>
                <v-flex xs6 class="pr-1">
                  <v-text-field
                    v-model="formData.busChangePhoneNumber"
                    label="Bus Change Phone Number"
                    prepend-inner-icon="mdi-phone"
                    outlined
                    dense
                    class="mb-3"
                    autocomplete="off"
                    :rules="[rules.phoneNumber]"
                    mask="###-###-####"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 class="pl-1">
                  <v-text-field
                    v-model="formData.busChangeName"
                    label="Bus Change Family Name"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    hide-details
                    class="mb-3"
                    autocomplete="off"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap class="mb-3">
                <v-flex xs6 class="pr-1">
                  <v-autocomplete
                    v-model="selectedPickupRoute"
                    :items="filteredPickupRoutes"
                    :filter="customRouteFilter"
                    item-text="description"
                    item-value="id"
                    label="Pickup Route"
                    prepend-inner-icon="mdi-bus-clock"
                    outlined
                    dense
                    clearable
                    hide-details
                    :loading="loadingBusRoutes"
                    return-object
                    @change="onPickupRouteChange"
                    :menu-props="{ bottom: true, offsetY: true }"
                  >
                    <template v-slot:selection="{ item }">
                      <div class="route-selection-wrapper">
                        <div class="route-selection-main">
                          {{ item.description }}
                          <span
                            v-if="item.busNumber"
                            class="route-detail-chip"
                            :style="getRouteChipStyle(item)"
                          >
                            Bus #{{ item.busNumber }}
                          </span>
                        </div>
                        <div v-if="item.driver" class="route-selection-driver">
                          <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                        </div>
                      </div>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-tile-content
                        class="route-list-item-bus-change-request"
                        :style="
                          getRouteColor(item.color)
                            ? `border-left: 4px solid ${getRouteColor(
                                item.color
                              )}`
                            : ''
                        "
                      >
                        <v-list-tile-title>
                          <strong>{{ item.description }}</strong>
                        </v-list-tile-title>
                        <v-list-tile-sub-title>
                          <span v-if="item.busNumber" class="mr-2">
                            <v-icon x-small>mdi-bus</v-icon> Bus #{{
                              item.busNumber
                            }}
                          </span>
                          <span v-if="item.driver" class="mr-2">
                            <v-icon x-small>mdi-account</v-icon>
                            {{ item.driver }}
                          </span>
                          <span v-if="item.color" class="mr-2">
                            <v-icon
                              x-small
                              :style="
                                getRouteColor(item.color)
                                  ? `color: ${getRouteColor(item.color)}`
                                  : ''
                              "
                              >mdi-circle</v-icon
                            >
                            {{ item.color }}
                          </span>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex xs6 class="pl-1">
                  <v-autocomplete
                    v-model="selectedDropoffRoute"
                    :items="filteredDropoffRoutes"
                    :filter="customRouteFilter"
                    item-text="description"
                    item-value="id"
                    label="Dropoff Route"
                    prepend-inner-icon="mdi-bus-marker"
                    outlined
                    dense
                    clearable
                    hide-details
                    :loading="loadingBusRoutes"
                    return-object
                    :menu-props="{ bottom: true, offsetY: true }"
                  >
                    <template v-slot:selection="{ item }">
                      <div class="route-selection-wrapper">
                        <div class="route-selection-main">
                          {{ item.description }}
                          <span
                            v-if="item.busNumber"
                            class="route-detail-chip"
                            :style="getRouteChipStyle(item)"
                          >
                            Bus #{{ item.busNumber }}
                          </span>
                        </div>
                        <div v-if="item.driver" class="route-selection-driver">
                          <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                        </div>
                      </div>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-tile-content
                        class="route-list-item-bus-change-request"
                        :style="
                          getRouteColor(item.color)
                            ? `border-left: 4px solid ${getRouteColor(
                                item.color
                              )}`
                            : ''
                        "
                      >
                        <v-list-tile-title>
                          <strong>{{ item.description }}</strong>
                        </v-list-tile-title>
                        <v-list-tile-sub-title>
                          <span v-if="item.busNumber" class="mr-2">
                            <v-icon x-small>mdi-bus</v-icon> Bus #{{
                              item.busNumber
                            }}
                          </span>
                          <span v-if="item.driver" class="mr-2">
                            <v-icon x-small>mdi-account</v-icon>
                            {{ item.driver }}
                          </span>
                          <span v-if="item.color" class="mr-2">
                            <v-icon
                              x-small
                              :style="
                                getRouteColor(item.color)
                                  ? `color: ${getRouteColor(item.color)}`
                                  : ''
                              "
                              >mdi-palette</v-icon
                            >
                            {{ item.color }}
                          </span>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </template>
                  </v-autocomplete>
                </v-flex>
              </v-layout>

              <v-layout row wrap class="mb-3">
                <v-flex xs6 class="pr-1">
                  <v-text-field
                    v-model="formData.geoPhoneNumber"
                    label="Geo Tracking Phone Number"
                    prepend-inner-icon="mdi-phone-in-talk"
                    outlined
                    dense
                    hide-details
                    :rules="[rules.phoneNumber]"
                    mask="###-###-####"
                    autocomplete="off"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 class="pl-1">
                  <v-textarea
                    v-model="formData.note"
                    label="Note"
                    prepend-inner-icon="mdi-note-text"
                    outlined
                    dense
                    hide-details
                    rows="2"
                  ></v-textarea>
                </v-flex>
              </v-layout>
            </v-flex>

            <!-- Right Column: Parent Messages -->
            <v-flex xs12 md3 class="pl-2">
              <div class="section-header mb-3">
                <v-icon x-small class="mr-1">mdi-message-text</v-icon>
                Messages
              </div>
              <div
                v-if="isPendingRequest(selectedRequest)"
                class="pending-request-indicator mb-2"
              >
                Last request is awaiting action.
              </div>
              <v-card outlined class="pa-2 comments-card">
                <div
                  v-if="loadingBusChangeComments"
                  class="d-flex align-center grey--text text--darken-1"
                >
                  <v-progress-circular
                    size="18"
                    width="2"
                    indeterminate
                    color="success"
                    class="mr-2"
                  ></v-progress-circular>
                  Loading...
                </div>
                <div
                  v-else-if="busChangeComments.length === 0"
                  class="grey--text text--darken-1"
                >
                  No messages.
                </div>
                <div v-else>
                  <div
                    v-for="(comment, index) in busChangeComments"
                    :key="comment.id || comment.ID || comment.callUUID || index"
                    class="comment-entry"
                  >
                    <div class="comment-meta">
                      <span class="font-weight-medium">
                        {{ getCommentEnteredBy(comment) || "Parent" }}
                      </span>
                      <span class="mx-1">-</span>
                      <span>{{
                        formatDate(getCommentEnteredOn(comment))
                      }}</span>
                    </div>
                    <div
                      v-if="
                        isCommentAwaitingResponse(
                          comment,
                          index,
                          busChangeComments
                        )
                      "
                      class="comment-awaiting mt-1"
                    >
                      Action required
                    </div>
                    <div
                      v-if="getCommentCallerID(comment)"
                      class="comment-caller grey--text text--darken-1"
                    >
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ formatPhone(getCommentCallerID(comment)) }}
                    </div>
                    <div
                      v-if="getCommentText(comment)"
                      class="comment-text grey--text text--darken-3"
                    >
                      {{ getCommentText(comment) }}
                    </div>
                    <audio
                      v-if="getCommentRecordingUrl(comment)"
                      controls
                      controlsList="nodownload"
                      :src="getCommentRecordingUrl(comment)"
                      class="comment-audio mt-2"
                    ></audio>
                  </div>
                </div>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeDialog">
            Cancel
          </v-btn>
          <v-btn
            v-if="isPendingRequest(selectedRequest)"
            color="error"
            dark
            :loading="deletingRequest"
            :disabled="saving"
            @click="promptDeleteRequest"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete Request
          </v-btn>
          <v-btn
            color="success"
            dark
            :loading="saving"
            :disabled="deletingRequest"
            @click="saveRequest"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Complete & Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account Search Dialog -->
    <v-dialog v-model="accountSearchDialog" max-width="800">
      <v-card>
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-account-search</v-icon
          >
          <span class="dialog-title">Search Account</span>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text my-0"
            icon
            dark
            @click="closeAccountSearchDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Phone Number Search -->
          <v-layout row wrap class="mb-1">
            <v-flex xs12 class="pr-2">
              <v-text-field
                v-model="dialogPhoneSearch"
                label="Search by Phone Number"
                prepend-inner-icon="mdi-phone"
                outlined
                dense
                clearable
                mask="###-###-####"
                @input="searchDialogByPhone"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Search Name Mode Toggle -->
          <v-layout row wrap align-center class="mb-2">
            <v-flex xs12 class="pr-2">
              <div class="caption grey--text mb-1">Or Search By Name</div>
              <v-btn-toggle
                v-model="dialogSearchNameMode"
                mandatory
                class="search-toggle"
              >
                <v-btn flat small value="yiddish">אידיש</v-btn>
                <v-btn flat small value="english">English</v-btn>
              </v-btn-toggle>
            </v-flex>
          </v-layout>

          <!-- Account Search Fields -->
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogLastNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'לעצטע נאמען'
                    : 'Last Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogFirstNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'טאטעס נאמען'
                    : 'Father Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Address Search -->
          <v-layout row wrap class="mb-2">
            <v-flex xs12>
              <div class="caption grey--text mb-1">Or Search By Address</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogHouseNumberSearch"
                label="House Number"
                prepend-inner-icon="mdi-home"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogStreetSearch"
                label="Street Name"
                prepend-inner-icon="mdi-road"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Account List -->
          <div class="account-list-container" v-if="dialogAccounts.length > 0">
            <div class="caption grey--text my-2 px-2">
              Select an account ({{ dialogAccounts.length }} found)
            </div>
            <v-list dense class="account-list">
              <v-list-tile
                v-for="account in dialogAccounts"
                :key="account.accountID"
                @click="onAccountSelected(account)"
                class="account-item"
              >
                <v-list-tile-avatar>
                  <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    <strong>{{ account.name }}</strong>
                    <span class="grey--text ml-2">({{ account.hname }})</span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    Account ID: {{ account.accountID }}
                    <span v-if="account.homePhone" class="ml-1">
                      | {{ account.homePhone }}
                    </span>
                    <span v-if="account.cellPhone" class="ml-1">
                      | {{ account.cellPhone }}
                    </span>
                    <span v-if="account.address1" class="ml-1">
                      | {{ account.address1 }}
                    </span>
                    <span v-if="account.busAddress1" class="ml-1">
                      | Bus: {{ account.busAddress1 }}
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>

          <!-- No Accounts Message -->
          <v-alert
            v-if="dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            type="warning"
            outline
            dense
            value="true"
            class="mb-3"
          >
            No accounts found. Please try a different search.
          </v-alert>

          <!-- Empty state -->
          <div
            v-if="!dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            class="text-center px-2 pb-3 grey--text"
          >
            <v-icon large color="grey lighten-1">mdi-account-search</v-icon>
            <div class="mt-2">
              Search for an account by phone, name, or address
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Incomplete Route Confirmation Dialog -->
    <v-dialog v-model="incompleteRouteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Incomplete Route Selection
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ getIncompleteRouteMessage() }}
          </v-alert>
          <div class="mt-3">Do you want to continue saving anyway?</div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="incompleteRouteDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="warning" dark @click="confirmSaveWithIncompleteRoute">
            Continue Saving
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Request Confirmation Dialog -->
    <v-dialog v-model="deleteRequestConfirmDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-delete-alert</v-icon>
          Delete Bus Change Request
        </v-card-title>
        <v-card-text class="pt-3 pb-2">
          <v-alert
            type="warning"
            outline
            value="true"
            class="delete-warning-banner mb-3"
          >
            <strong>Permanent action:</strong> deleting this request cannot be
            undone.
          </v-alert>
          <div v-if="selectedRequest" class="delete-summary-card">
            <div class="delete-summary-title">
              <v-icon small color="error" class="mr-1">mdi-bus-alert</v-icon>
              Request Details
            </div>
            <div class="delete-summary-grid">
              <div class="delete-summary-item">
                <div class="delete-label">Student</div>
                <div class="delete-value">
                  {{ selectedRequest.studentName || "-" }}
                  <span v-if="selectedRequest.studentID">
                    ({{ selectedRequest.studentID }})
                  </span>
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Branch / Class</div>
                <div class="delete-value">
                  {{ selectedRequest.branch || "-" }} /
                  {{ selectedRequest.class || "-" }}-{{
                    selectedRequest.section || "-"
                  }}
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Date Range</div>
                <div class="delete-value">
                  {{ formData.startDate || "-" }} to
                  {{ formData.endDate || "Open" }}
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Run</div>
                <div class="delete-value">
                  {{ formatPeriodLabel(formData.startPeriod) }} to
                  {{ formatPeriodLabel(formData.endPeriod) }}
                </div>
              </div>
              <div class="delete-summary-item delete-summary-item--full">
                <div class="delete-label">Address</div>
                <div class="delete-value">
                  {{ formData.busChangeAddress || "-" }}
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="deleteRequestConfirmDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            dark
            :loading="deletingRequest"
            @click="deleteRequest"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Confirmation Dialog -->
    <v-dialog v-model="notificationConfirmDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="headline success white--text">
          <v-icon left dark>mdi-information</v-icon>
          Notification Will Be Sent
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert
            value="true"
            type="info"
            outline
            class="mb-3"
            v-if="selectedRequest"
          >
            <div class="font-weight-bold mb-1">
              <v-icon small color="info" class="mr-1">
                {{
                  selectedRequest.notificationType === "CALL"
                    ? "mdi-phone"
                    : "mdi-message-text"
                }}
              </v-icon>
              {{
                selectedRequest.notificationType === "CALL"
                  ? "A phone call"
                  : "A text message"
              }}
              will automatically be sent to:
            </div>
            <div class="ml-4">
              {{ formatPhoneNumber(selectedRequest.notificationPhoneNumber) }}
            </div>
          </v-alert>
          <div class="grey--text text--darken-1">
            The bus change will be saved and the notification will be sent
            automatically.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" dark @click="proceedWithNotification">
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Notification Fields Dialog -->
    <v-dialog
      v-model="missingNotificationFieldsDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Missing Notification Information
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outlined class="mb-3" v-if="selectedRequest">
            <div class="font-weight-bold mb-2">
              Cannot send notification - missing required fields:
            </div>
            <ul class="ml-4">
              <li v-if="!selectedRequest.notificationPhoneNumber">
                Notification Phone Number
              </li>
              <li v-if="!selectedRequest.notificationType">
                Notification Type
              </li>
            </ul>
          </v-alert>
          <div class="mt-3">
            Would you like to save without sending a notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="warning" @click="saveWithoutNotification">
            Save Without Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cleared Notification Warning Dialog (IVR) -->
    <v-dialog
      v-model="clearedNotificationWarningDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Notification Fields Cleared
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" outlined class="mb-3">
            <div class="font-weight-bold mb-2 grey--text text--darken-4">
              This request originally had notification information
            </div>
            <div class="grey--text text--darken-3">
              By not having notification fields, the user will NOT receive a
              notification about this bus change.
            </div>
          </v-alert>
          <div class="mt-3">
            Do you want to continue saving without notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelClearedNotificationWarning">
            Cancel
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithoutNotification">
            Save Without Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bus Change View Dialog for Completed Requests -->
    <bus-change-view-dialog
      :dialog.sync="viewDialog"
      :busChangeId="viewBusChangeId"
      :isViewOnly="false"
      :sendNotificationOnSave="viewDialogSendNotificationOnSave"
      @saved="onBusChangeSaved"
      @close="onViewDialogClose"
    ></bus-change-view-dialog>
  </div>
</template>

<script>
import moment from "moment";
import BusChangeViewDialog from "./BusChangeViewDialog.vue";

export default {
  components: {
    BusChangeViewDialog,
  },

  data: () => ({
    title: "Bus Change Requests",
    actions: [],
    url: "",
    showCompletedRequests: false,
    showDeletedRequests: false,

    // Dialog state
    processDialog: false,
    saving: false,
    deletingRequest: false,
    selectedRequest: null,
    busChangeComments: [],
    loadingBusChangeComments: false,

    // View dialog for completed requests
    viewDialog: false,
    viewBusChangeId: null,
    viewDialogSendNotificationOnSave: false,

    // Account search dialog
    accountSearchDialog: false,
    dialogPhoneSearch: "",
    dialogFirstNameSearch: "",
    dialogLastNameSearch: "",
    dialogHouseNumberSearch: "",
    dialogStreetSearch: "",
    dialogAccounts: [],
    dialogSearchNameMode: "yiddish",
    dialogShowNoAccountsMessage: false,
    dialogSearchDebounceTimer: null,

    // Incomplete route confirmation dialog
    incompleteRouteDialog: false,
    skipRouteWarning: false,
    deleteRequestConfirmDialog: false,

    // Bus change notification dialogs and state
    notificationConfirmDialog: false,
    missingNotificationFieldsDialog: false,
    clearedNotificationWarningDialog: false,
    skipNotification: false,
    originalPickupRouteID: null,
    originalDropoffRouteID: null,
    originalNotificationPhone: null,
    originalNotificationType: null,
    savedBusChangeId: null,
    sendingNotification: false,

    // Date pickers
    startDateMenu: false,
    endDateMenu: false,

    // Bus routes
    busRoutes: [],
    filteredPickupRoutes: [],
    filteredDropoffRoutes: [],
    selectedPickupRoute: null,
    selectedDropoffRoute: null,
    loadingBusRoutes: false,
    selectedGradeDeadlines: [],
    busChangeGrades: [],

    // Form data
    formData: {
      startDate: "",
      endDate: "",
      startPeriod: "",
      endPeriod: "",
      busChangeAddress: "",
      busChangePhoneNumber: "",
      geoPhoneNumber: "",
      busChangeName: "",
      busChangeAccountID: "",
      note: "",
    },
    periodOptions: [
      { label: "Morning Pickup", value: "AM" },
      { label: "Afternoon Dropoff", value: "PM" },
    ],

    // Validation rules
    rules: {
      required: (value) => !!value || "Required.",
      phoneNumber: (v) => {
        if (!v) return true;
        const digits = v.replace(/[-\s()]/g, "");
        return (
          ([10, 11].includes(digits.length) && /^\d+$/.test(digits)) ||
          "Invalid Phone Number"
        );
      },
    },

    columns: [
      {
        field: "statusIcon",
        label: "",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        cssClass: "width-25",
        icon: (value) => {
          if (!value) return "";
          switch (value.trim().toUpperCase()) {
            case "PENDING":
              return "mdi-clock";
            case "COMPLETED":
              return "mdi-check-circle";
            case "DELETED":
              return "mdi-delete";
            default:
              return "mdi-help-circle";
          }
        },
        iconColor: (value) => {
          if (!value) return "";
          switch (value.trim().toUpperCase()) {
            case "PENDING":
              return "#ff9800";
            case "COMPLETED":
              return "rgb(92, 184, 96)";
            case "DELETED":
              return "#f44336";
            default:
              return "#9e9e9e";
          }
        },
      },
      {
        label: "Status",
        field: "status",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortIndex: 0,
        sortOrder: "asc",
        calculateSortValue: (data) => {
          const order = { PENDING: 1, COMPLETED: 2, DELETED: 3 };
          const status = data.status ? data.status.toUpperCase() : "";
          return order[status] || 4;
        },
      },
      {
        label: "Date",
        field: "date",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy hh:mm a",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortIndex: 1,
        sortOrder: "desc",
      },
      {
        label: "Student ID",
        field: "studentID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Student Name",
        field: "studentName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Student Name (Heb)",
        field: "studentNameHeb",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Family ID",
        field: "familyID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Building",
        field: "building",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Branch",
        field: "branch",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Class",
        field: "class",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Section",
        field: "section",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Source",
        field: "source",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Caller ID",
        field: "callerID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Recording",
        template: "link-audio-cell-template",
        field: "recordingURL",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Cell Phone",
        field: "cellPhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Address",
        field: "busAddress1",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Notification Phone",
        field: "notificationPhoneNumber",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Notification Type",
        field: "notificationType",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Completed By",
        field: "completedBy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Completed On",
        field: "completedOn",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy hh:mm a",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),

  computed: {
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    getRecordingUrl() {
      if (!this.selectedRequest) return null;
      return (
        this.selectedRequest.recordingURL ||
        this.selectedRequest.RecordingURL ||
        this.selectedRequest.recordingUrl ||
        null
      );
    },
    deadlineWarningMessage() {
      if (!this.selectedRequest) return null;
      return this.checkDeadlineWarning(
        this.selectedRequest.class,
        this.selectedRequest.branch,
        this.formData.startDate
      );
    },
  },

  watch: {
    selectedRequest: {
      handler(request) {
        if (request && request.building) {
          this.filterRoutesByBuilding();
        }
      },
      deep: true,
    },
    dialogSearchNameMode() {
      // Re-search when switching between English and Yiddish
      if (this.dialogFirstNameSearch || this.dialogLastNameSearch) {
        this.searchDialogByName();
      }
    },
  },

  methods: {
    buildBusChangeRequestsUrl() {
      const branchParam = this.userBranch ? this.userBranch : "";
      const includeCompleted = this.showCompletedRequests ? "true" : "";
      const includeDeleted = this.showDeletedRequests ? "true" : "";
      return (
        "busChangeRequests?branch=" +
        branchParam +
        "&includeCompleted=" +
        includeCompleted +
        "&includeDeleted=" +
        includeDeleted
      );
    },

    reloadBusChangeRequestsGrid() {
      this.url = this.buildBusChangeRequestsUrl();
      this.$refs.grid.reloadDataSource(this.url);
    },

    async beforeSetupGrid() {
      this.showCompletedRequests = false;
      this.showDeletedRequests = false;
      this.url = this.buildBusChangeRequestsUrl();

      this.actions.push({
        text: "Show Completed",
        type: "switch",
        align: "left",
        action: this.onShowCompletedChanged,
      });

      this.actions.push({
        text: "Show Deleted",
        type: "switch",
        align: "left",
        action: this.onShowDeletedChanged,
      });
    },

    onShowCompletedChanged(value) {
      this.showCompletedRequests = !!value;
      this.reloadBusChangeRequestsGrid();
    },

    onShowDeletedChanged(value) {
      this.showDeletedRequests = !!value;
      this.reloadBusChangeRequestsGrid();
    },

    async onRowClicked(row) {
      const status = row && row.status ? row.status.toUpperCase() : "";
      const busChangeId =
        row && row.busChangeID ? row.busChangeID : row.busChangeId;

      // Open bus change dialog when bus change already exists
      if (
        busChangeId &&
        (status === "COMPLETED" || status === "PENDING" || status === "DELETED")
      ) {
        if (busChangeId) {
          this.viewDialogSendNotificationOnSave = status === "PENDING";
          this.viewBusChangeId = busChangeId;
          this.viewDialog = true;
        }
        return;
      }

      this.selectedRequest = row;
      this.resetForm();
      await this.loadSelectedGradeDeadlines(row.class, row.branch);

      // Pre-fill all available fields from the request row
      const rawStartDate = row.startDate || row.StartDate || "";
      if (rawStartDate) {
        this.formData.startDate = moment(rawStartDate).format("YYYY-MM-DD");
      }

      const rawEndDate = row.endDate || row.EndDate || "";
      if (rawEndDate) {
        this.formData.endDate = moment(rawEndDate).format("YYYY-MM-DD");
      }

      this.formData.startPeriod = row.startPeriod || row.StartPeriod || "";
      this.formData.endPeriod = row.endPeriod || row.EndPeriod || "";

      this.originalPickupRouteID =
        row.pickupRouteID || row.PickupRouteID || null;
      this.originalDropoffRouteID =
        row.dropoffRouteID || row.DropoffRouteID || null;

      this.formData.busChangeName =
        row.busChangeName || row.BusChangeName || "";
      this.formData.busChangeAddress =
        row.busChangeAddress || row.BusChangeAddress || "";
      this.formData.busChangePhoneNumber =
        row.busChangePhoneNumber || row.BusChangePhoneNumber || "";
      this.formData.note = row.note || row.Note || "";

      const requestGeoPhoneNumber =
        row.geoPhoneNumber || row.GeoPhoneNumber || "";
      if (requestGeoPhoneNumber) {
        this.formData.geoPhoneNumber = requestGeoPhoneNumber;
      }

      await this.loadBusChangeComments(this.getBusChangeRequestId(row));

      // Fetch full student info to get teacher details
      if (row.studentID) {
        try {
          const studentInfo = await this.$http.get(
            `api/students/getStudent?studentID=${row.studentID}`
          );
          if (studentInfo) {
            this.selectedRequest = {
              ...this.selectedRequest,
              teacherH: studentInfo.teacherH,
              teacherH2: studentInfo.teacherH2,
            };
          }
        } catch (e) {
          // Continue without teacher info if API call fails
        }
      }

      // Load bus routes if not loaded
      if (!this.busRoutes.length) {
        await this.loadBusRoutes();
      } else {
        this.filterRoutesByBuilding();
      }

      if (this.originalPickupRouteID) {
        const pickupRoute = this.busRoutes.find(
          (r) => r.id === this.originalPickupRouteID
        );
        this.selectedPickupRoute = pickupRoute || null;
      }
      if (this.originalDropoffRouteID) {
        const dropoffRoute = this.busRoutes.find(
          (r) => r.id === this.originalDropoffRouteID
        );
        this.selectedDropoffRoute = dropoffRoute || null;
      }

      this.processDialog = true;
    },

    resetForm() {
      this.formData = {
        startDate: "",
        endDate: "",
        startPeriod: "",
        endPeriod: "",
        busChangeAddress: "",
        busChangePhoneNumber: "",
        geoPhoneNumber: "",
        busChangeName: "",
        busChangeAccountID: "",
        note: "",
      };
      this.selectedPickupRoute = null;
      this.selectedDropoffRoute = null;
      this.skipRouteWarning = false;
      this.originalPickupRouteID = null;
      this.originalDropoffRouteID = null;
      this.originalNotificationPhone = null;
      this.originalNotificationType = null;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      this.selectedGradeDeadlines = [];
      this.busChangeComments = [];
      this.loadingBusChangeComments = false;
    },

    closeDialog() {
      this.processDialog = false;
      this.deleteRequestConfirmDialog = false;
      this.selectedRequest = null;
      this.deletingRequest = false;
      this.busChangeComments = [];
      this.loadingBusChangeComments = false;
      this.resetForm();
    },

    getBusChangeRequestId(request) {
      if (!request) {
        return null;
      }

      if (request.id) return request.id;
      if (request.busChangeRequestID) return request.busChangeRequestID;
      if (request.busChangeRequestId) return request.busChangeRequestId;
      if (request.BusChangeRequestID) return request.BusChangeRequestID;
      return null;
    },

    isPendingRequest(request) {
      const status =
        request && request.status ? request.status.toUpperCase() : "";
      return status === "PENDING";
    },

    getPendingRevertedToDate(request) {
      if (!request) return null;
      return (
        request.statusRevertedToPendingOn ||
        request.StatusRevertedToPendingOn ||
        null
      );
    },

    isCommentAwaitingResponse(comment, index, comments) {
      if (!this.isPendingRequest(this.selectedRequest)) return false;
      const revertedToPendingOn = this.getPendingRevertedToDate(
        this.selectedRequest
      );

      if (revertedToPendingOn) {
        const enteredOn = this.getCommentEnteredOn(comment);
        if (!enteredOn) return false;
        return moment(enteredOn).isSameOrAfter(moment(revertedToPendingOn));
      }

      return comments && index === comments.length - 1;
    },

    promptDeleteRequest() {
      this.deleteRequestConfirmDialog = true;
    },

    async deleteRequest() {
      const requestId = this.getBusChangeRequestId(this.selectedRequest);
      if (!requestId) {
        this.$notify({
          text: "Unable to delete request: missing request ID",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.deletingRequest = true;
      try {
        this.deleteRequestConfirmDialog = false;
        await this.$http.post("api/BusChangeRequests/Delete?id=" + requestId);
        this.$notify({
          text: "Bus change request deleted successfully",
          group: "notification",
          type: "success",
        });
        this.closeDialog();
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.$notify({
          text: "Failed to delete bus change request",
          group: "notification",
          type: "error",
        });
      } finally {
        this.deletingRequest = false;
      }
    },

    getCommentText(comment) {
      if (!comment) return "";
      return comment.commentText || comment.CommentText || "";
    },

    getCommentEnteredBy(comment) {
      if (!comment) return "";
      const enteredBy = comment.enteredBy || comment.EnteredBy || "";
      const familyId = this.selectedRequest
        ? this.selectedRequest.familyID || this.selectedRequest.FamilyID || ""
        : "";
      const normalizeIdentity = (value) =>
        value
          ? value
              .toString()
              .trim()
              .replace(/\s+/g, "")
              .toUpperCase()
          : "";
      if (
        enteredBy &&
        familyId &&
        normalizeIdentity(enteredBy) === normalizeIdentity(familyId)
      ) {
        return "Parent";
      }
      return enteredBy;
    },

    getCommentEnteredOn(comment) {
      if (!comment) return null;
      return comment.enteredOn || comment.EnteredOn || null;
    },

    getCommentCallerID(comment) {
      if (!comment) return "";
      return comment.callerID || comment.CallerID || "";
    },

    getCommentRecordingUrl(comment) {
      if (!comment) return null;
      return (
        comment.recordingURL ||
        comment.recordingUrl ||
        comment.RecordingURL ||
        null
      );
    },

    getPeriodOrder(period) {
      if (period === "PM") return 2;
      return 1;
    },

    formatPeriodLabel(period) {
      if (!period) return "-";
      const normalized = period
        .toString()
        .trim()
        .toUpperCase();
      if (normalized === "AM") return "Morning Pickup";
      if (normalized === "PM") return "Afternoon Dropoff";
      return period;
    },

    getIncompleteRouteMessage() {
      const hasPickup =
        this.selectedPickupRoute && this.selectedPickupRoute.id;
      const hasDropoff =
        this.selectedDropoffRoute && this.selectedDropoffRoute.id;

      if (hasPickup && !hasDropoff) {
        return "You have only selected the Pickup route.";
      }
      if (!hasPickup && hasDropoff) {
        return "You have only selected the Dropoff route.";
      }
      return "You have not selected any route.";
    },

    isEndPeriodBeforeStartPeriod(startPeriod, endPeriod) {
      return this.getPeriodOrder(endPeriod) < this.getPeriodOrder(startPeriod);
    },

    async loadBusChangeComments(busChangeRequestId) {
      this.busChangeComments = [];

      if (!busChangeRequestId) {
        return;
      }

      this.loadingBusChangeComments = true;
      try {
        const response = await this.$http.get(
          `api/BusChange/GetBusChangeComments?busChangeRequestId=${busChangeRequestId}`
        );
        this.busChangeComments = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeComments = [];
      } finally {
        this.loadingBusChangeComments = false;
      }
    },

    async loadBusRoutes() {
      this.loadingBusRoutes = true;
      try {
        this.busRoutes = await this.$http.get("api/buschange/GetBusRoutes");
        this.filterRoutesByBuilding();
      } catch (e) {
        this.busRoutes = [];
        this.filteredPickupRoutes = [];
        this.filteredDropoffRoutes = [];
      } finally {
        this.loadingBusRoutes = false;
      }
    },

    filterRoutesByBuilding() {
      if (!this.selectedRequest || !this.selectedRequest.building) {
        this.filteredPickupRoutes = this.busRoutes.filter(
          (r) => r.direction === "Pickup"
        );
        this.filteredDropoffRoutes = this.busRoutes.filter(
          (r) => r.direction === "Dropoff"
        );
        return;
      }

      const studentBuilding = this.selectedRequest.building;
      this.filteredPickupRoutes = this.busRoutes.filter((r) => {
        if (r.direction !== "Pickup") return false;
        if (!r.buildings) return true;
        const routeBuildings = r.buildings
          .split(",")
          .map((b) => b.trim().toUpperCase());
        return routeBuildings.includes(
          studentBuilding.toString().toUpperCase()
        );
      });

      this.filteredDropoffRoutes = this.busRoutes.filter((r) => {
        if (r.direction !== "Dropoff") return false;
        if (!r.buildings) return true;
        const routeBuildings = r.buildings
          .split(",")
          .map((b) => b.trim().toUpperCase());
        return routeBuildings.includes(
          studentBuilding.toString().toUpperCase()
        );
      });
    },

    customRouteFilter(item, queryText, itemText) {
      if (!queryText) return true;
      const search = queryText.toUpperCase();
      const description = (item.description || "").toUpperCase();
      const busNumber = (item.busNumber || "").toString().toUpperCase();
      const driver = (item.driver || "").toUpperCase();
      const color = (item.color || "").toUpperCase();
      const timeStart = (item.timeStart || "").toUpperCase();
      const routeIdentifier = (item.routeIdentifier || "").toUpperCase();

      return (
        description.includes(search) ||
        busNumber.includes(search) ||
        driver.includes(search) ||
        color.includes(search) ||
        timeStart.includes(search) ||
        routeIdentifier.includes(search)
      );
    },

    onPickupRouteChange(pickupRoute) {
      if (!pickupRoute || !this.filteredDropoffRoutes.length) {
        return;
      }

      const pickupBuildings = pickupRoute.buildings
        ? pickupRoute.buildings.split(",").map((b) => b.trim())
        : [];
      const pickupGrades = pickupRoute.grades
        ? pickupRoute.grades.split(",").map((g) => g.trim())
        : [];

      // Tier 1: Exact match
      let matchingRoute = this.filteredDropoffRoutes.find((route) => {
        if (
          route.busNumber !== pickupRoute.busNumber ||
          route.driver !== pickupRoute.driver ||
          route.driver_Bus_ID !== pickupRoute.driver_Bus_ID
        ) {
          return false;
        }

        const routeBuildings = route.buildings
          ? route.buildings.split(",").map((b) => b.trim())
          : [];
        const routeGrades = route.grades
          ? route.grades.split(",").map((g) => g.trim())
          : [];

        const buildingsMatch =
          pickupBuildings.length === routeBuildings.length &&
          pickupBuildings.every((b) => routeBuildings.includes(b));

        const gradesMatch =
          pickupGrades.length === routeGrades.length &&
          pickupGrades.every((g) => routeGrades.includes(g));

        return buildingsMatch && gradesMatch;
      });

      // Tier 2: Overlap match
      if (!matchingRoute) {
        matchingRoute = this.filteredDropoffRoutes.find((route) => {
          if (
            route.busNumber !== pickupRoute.busNumber ||
            route.driver !== pickupRoute.driver ||
            route.driver_Bus_ID !== pickupRoute.driver_Bus_ID
          ) {
            return false;
          }

          const routeBuildings = route.buildings
            ? route.buildings.split(",").map((b) => b.trim())
            : [];
          const routeGrades = route.grades
            ? route.grades.split(",").map((g) => g.trim())
            : [];

          const buildingsOverlap =
            pickupBuildings.length === 0 ||
            routeBuildings.length === 0 ||
            this.hasOverlap(pickupRoute.buildings, route.buildings);

          const gradesOverlap =
            pickupGrades.length === 0 ||
            routeGrades.length === 0 ||
            this.hasOverlap(pickupRoute.grades, route.grades);

          return buildingsOverlap && gradesOverlap;
        });
      }

      // Tier 3: Loose match
      if (!matchingRoute) {
        matchingRoute = this.filteredDropoffRoutes.find((route) => {
          if (
            route.busNumber !== pickupRoute.busNumber ||
            route.driver !== pickupRoute.driver
          ) {
            return false;
          }

          const gradesOverlap =
            !pickupRoute.grades ||
            !route.grades ||
            this.hasOverlap(pickupRoute.grades, route.grades);

          return gradesOverlap;
        });
      }

      if (matchingRoute) {
        this.selectedDropoffRoute = matchingRoute;
      }
    },

    hasOverlap(str1, str2) {
      if (!str1 || !str2) return false;
      const arr1 = str1
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter((s) => s);
      const arr2 = str2
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter((s) => s);
      return arr1.some((item) => arr2.includes(item));
    },

    getRouteColor(colorName) {
      if (!colorName) return null;

      const colorUpper = colorName.toUpperCase().trim();
      const colorMap = {
        BEIGE: "#F5F5DC",
        BLACK: "#000000",
        BLUE: "#0000FF",
        BROWN: "#8B4513",
        GOLD: "#FFD700",
        GREEN: "#008000",
        ORANGE: "#FFA500",
        PINK: "#FFC0CB",
        PURPLE: "#800080",
        RED: "#FF0000",
        SILVER: "#C0C0C0",
        WHITE: "#E0E0E0",
        YELLOW: "#FFFF00",
        "DARK BLUE": "#00008B",
        "DARK GREEN": "#006400",
        "DARK PINK": "#FF1493",
        "DARK PURPLE": "#4B0082",
        "LIGHT BLUE": "#ADD8E6",
        "LIGHT GREEN": "#90EE90",
        "LIGHT PINK": "#FFB6C1",
        "LIGHT PURPLE": "#DDA0DD",
        GRAY: "#808080",
        GREY: "#808080",
        CYAN: "#00FFFF",
        TEAL: "#008080",
        LIME: "#00FF00",
        INDIGO: "#4B0082",
        NAVY: "#000080",
        MAROON: "#800000",
        OLIVE: "#808000",
        VIOLET: "#EE82EE",
        TURQUOISE: "#40E0D0",
        CORAL: "#FF7F50",
        SALMON: "#FA8072",
        MINT: "#98FF98",
        LAVENDER: "#E6E6FA",
        PEACH: "#FFE5B4",
        SKY: "#87CEEB",
        ROSE: "#FF007F",
      };

      return colorMap[colorUpper] || "#5cb860";
    },

    getRouteChipStyle(route) {
      if (!route || !route.color) {
        return {
          backgroundColor: "#5cb860",
          color: "#ffffff",
        };
      }
      const bgColor = this.getRouteColor(route.color) || "#5cb860";
      const brightness = this.calculateBrightness(bgColor);
      const textColor = brightness > 128 ? "#000000" : "#ffffff";

      return {
        backgroundColor: bgColor,
        color: textColor,
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "0.75rem",
        marginLeft: "8px",
        display: "inline-block",
      };
    },

    calculateBrightness(hexColor) {
      const hex = hexColor.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return (r * 299 + g * 587 + b * 114) / 1000;
    },

    formatDate(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY hh:mm A");
    },

    formatPhone(phone) {
      if (!phone) return null;
      const digits = phone.replace(/\D/g, "");
      if (digits.length === 10) {
        return digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
      } else if (digits.length === 11 && digits.startsWith("1")) {
        return digits.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, "$1-$2-$3-$4");
      }
      return phone;
    },

    formatPhoneNumber(phoneNumber) {
      return this.formatPhone(phoneNumber);
    },

    openAccountSearch() {
      // Reset dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
      this.accountSearchDialog = true;
    },

    closeAccountSearchDialog() {
      this.accountSearchDialog = false;
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    async searchDialogByPhone(value) {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const phoneNumber = value ? value.trim() : "";

      if (!phoneNumber || phoneNumber.length < 10) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const accounts = await this.$http.get(
            `api/students/getAccountByPhoneNumber?phoneNumber=${phoneNumber}`
          );

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByName() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const lastName = this.dialogLastNameSearch
        ? this.dialogLastNameSearch.trim()
        : "";
      const firstName = this.dialogFirstNameSearch
        ? this.dialogFirstNameSearch.trim()
        : "";

      if (!lastName && !firstName) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const url = `api/students/getAccountsByName?last=${lastName}&first=${firstName}&isYiddish=${this
            .dialogSearchNameMode === "yiddish"}`;

          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByAddress() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const houseNumber = this.dialogHouseNumberSearch
        ? this.dialogHouseNumberSearch.trim()
        : "";
      const street = this.dialogStreetSearch
        ? this.dialogStreetSearch.trim()
        : "";

      if (!houseNumber && !street) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (houseNumber) params.append("houseNumber", houseNumber);
          if (street) params.append("street", street);
          if (this.userBranch) params.append("branch", this.userBranch);

          const url = `api/students/getAccountsByAddress?${params.toString()}`;
          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    onAccountSelected(account) {
      this.accountSearchDialog = false;
      this.formData.busChangeAccountID = account.accountID;
      const requestSource = this.selectedRequest
        ? this.selectedRequest.source || this.selectedRequest.Source || ""
        : "";
      const isIvrSource =
        requestSource &&
        requestSource
          .toString()
          .trim()
          .toUpperCase()
          .includes("IVR");

      // For IVR, always override. For non-IVR, only prefill if empty.
      if (isIvrSource || !this.formData.busChangeName) {
        this.formData.busChangeName =
          account.name ||
          ((account.lastName || "") + " " + (account.firstName || "")).trim();
      }
      if (account.address1 && (isIvrSource || !this.formData.busChangeAddress)) {
        this.formData.busChangeAddress = account.address1;
      }

      // Clear dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    validateForm() {
      if (!this.formData.startDate) {
        this.$notify({
          text: "Start Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.busChangeAddress) {
        this.$notify({
          text: "Bus Change Address is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.startPeriod) {
        this.$notify({
          text: "Starting Run is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.endPeriod) {
        this.$notify({
          text: "Ending After is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate phone numbers if they have values
      const validatePhone = (phoneNumber) => {
        if (!phoneNumber) return true; // Empty is ok (not required)
        const digits = phoneNumber.replace(/[-\s()]/g, "");
        return [10, 11].includes(digits.length) && /^\d+$/.test(digits);
      };

      if (
        this.formData.busChangePhoneNumber &&
        !validatePhone(this.formData.busChangePhoneNumber)
      ) {
        this.$notify({
          text:
            "Bus Change Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.formData.geoPhoneNumber &&
        !validatePhone(this.formData.geoPhoneNumber)
      ) {
        this.$notify({
          text:
            "Geo Tracking Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      return true;
    },

    // Notification dialog handlers
    proceedWithNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = true; // Flag to send notification after save
      this.saveRequest();
    },

    cancelSave() {
      this.notificationConfirmDialog = false;
      this.missingNotificationFieldsDialog = false;
      this.clearedNotificationWarningDialog = false;
      this.skipRouteWarning = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
    },

    saveWithoutNotification() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveRequest();
    },

    cancelClearedNotificationWarning() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = false;
      // User stays in edit mode
    },

    confirmSaveWithoutNotification() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveRequest();
    },

    async sendBusChangeNotification(busChangeId) {
      this.sendingNotification = true;
      try {
        const apiUrl = `https://broadcast-3t6ck5zrrq-ue.a.run.app/busChangeNotification/sendBusChangeNotification?busChangeId=${busChangeId}`;
        const response = await this.$http.post(apiUrl, {});

        if (response && !response.error) {
          this.$notify({
            text: "Bus change notification sent successfully",
            group: "notification",
            type: "success",
          });
        } else {
          this.$notify({
            text: `Notification could not be sent: ${response.error ||
              "Unknown error"}`,
            group: "notification",
            type: "warning",
          });
        }
      } catch (error) {
        this.$notify({
          text:
            "Failed to send bus change notification. The bus change was saved but notification failed.",
          group: "notification",
          type: "warning",
        });
      } finally {
        this.sendingNotification = false;
      }
    },

    async loadBusChangeGrades() {
      try {
        const response = await this.$http.get("api/busChange/getBusChangeGrades");
        this.busChangeGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeGrades = [];
      }
    },

    getGradeSetup(grade, branch) {
      if (!grade || !branch || !this.busChangeGrades.length) return null;
      return this.busChangeGrades.find(
        (g) =>
          g.grade &&
          g.grade.trim() === grade.trim() &&
          g.branch &&
          g.branch.trim() === branch.trim()
      );
    },

    async loadSelectedGradeDeadlines(grade, branch) {
      if (!this.busChangeGrades.length) {
        await this.loadBusChangeGrades();
      }
      const setup = this.getGradeSetup(grade, branch);
      if (!setup || !setup.id) {
        this.selectedGradeDeadlines = [];
        return;
      }
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + setup.id
        );
        this.selectedGradeDeadlines = Array.isArray(response) ? response : [];
      } catch (e) {
        this.selectedGradeDeadlines = [];
      }
    },

    checkDeadlineWarning(grade, branch, startDate) {
      if (!grade || !branch) return null;
      if (!startDate || !this.selectedGradeDeadlines.length) return null;

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      if (startDate > todayStr) return null;

      const startDateObj = new Date(startDate + "T00:00:00");
      const dayOfWeek = startDateObj.getDay();

      const exception = this.selectedGradeDeadlines.find(
        (d) => d.exceptionDate && d.exceptionDate.startsWith(startDate)
      );
      const deadline =
        exception ||
        this.selectedGradeDeadlines.find(
          (d) => !d.exceptionDate && d.dayOfWeek === dayOfWeek
        );
      if (!deadline || !deadline.deadline) return null;

      const currentTime =
        today.getHours() * 3600 + today.getMinutes() * 60 + today.getSeconds();
      const deadlineParts = deadline.deadline.split(":");
      if (deadlineParts.length < 2) return null;

      const deadlineSeconds =
        parseInt(deadlineParts[0]) * 3600 +
        parseInt(deadlineParts[1]) * 60 +
        (deadlineParts[2] ? parseFloat(deadlineParts[2]) : 0);
      if (currentTime <= deadlineSeconds) return null;

      let hours = parseInt(deadlineParts[0]);
      const minutes = deadlineParts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const deadlineFormatted = `${hours}:${minutes} ${ampm}`;
      return `Note: The deadline for bus changes has passed (${deadlineFormatted}).`;
    },

    notifyDeadlineWarningIfNeeded() {
      if (!this.deadlineWarningMessage) return;
      this.$notify({
        text: this.deadlineWarningMessage,
        group: "notification",
        type: "warning",
      });
    },

    async saveRequest() {
      if (!this.validateForm()) return;
      this.notifyDeadlineWarningIfNeeded();

      // Validate endDate is not before startDate
      if (this.formData.endDate && this.formData.startDate) {
        const startDate = moment(this.formData.startDate);
        const endDate = moment(this.formData.endDate);

        if (endDate.isBefore(startDate, "day")) {
          this.$notify({
            text: "End Date cannot be before Start Date",
            group: "notification",
            type: "error",
          });
          return;
        }

        if (
          endDate.isSame(startDate, "day") &&
          this.isEndPeriodBeforeStartPeriod(
            this.formData.startPeriod,
            this.formData.endPeriod
          )
        ) {
          this.$notify({
            text: "Ending After cannot be before Starting Run on the same day.",
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      // Check if notification fields are missing for IVR sources
      if (!this.skipNotification && this.selectedRequest) {
        const hadNotificationInfo =
          this.selectedRequest.notificationPhoneNumber ||
          this.selectedRequest.notificationType;

        // If request originally had notification info but it's now missing
        if (
          hadNotificationInfo &&
          (!this.selectedRequest.notificationPhoneNumber ||
            !this.selectedRequest.notificationType)
        ) {
          this.clearedNotificationWarningDialog = true;
          return;
        }
      }

      // Check for incomplete route selection warning rules
      if (!this.skipRouteWarning) {
        const hasPickup =
          this.selectedPickupRoute && this.selectedPickupRoute.id;
        const hasDropoff =
          this.selectedDropoffRoute && this.selectedDropoffRoute.id;
        const originalHasPickup = !!this.originalPickupRouteID;
        const originalHasDropoff = !!this.originalDropoffRouteID;
        const originallyNoRoutes = !originalHasPickup && !originalHasDropoff;
        const hasOnlyOneRoute =
          (hasPickup && !hasDropoff) || (!hasPickup && hasDropoff);

        if ((!hasPickup && !hasDropoff) || (hasOnlyOneRoute && originallyNoRoutes)) {
          this.incompleteRouteDialog = true;
          return;
        }
      }

      // Check if notification should be sent (at least one route is assigned)
      if (!this.skipNotification && this.selectedRequest) {
        const currentPickupID = this.selectedPickupRoute
          ? this.selectedPickupRoute.id
          : null;
        const currentDropoffID = this.selectedDropoffRoute
          ? this.selectedDropoffRoute.id
          : null;

        // Allow notification when at least one route is assigned
        const atLeastOneRouteSelected = currentPickupID || currentDropoffID;

        if (atLeastOneRouteSelected) {
          // Check if notification fields are filled
          const hasNotificationPhone = this.selectedRequest
            .notificationPhoneNumber;
          const hasNotificationType = this.selectedRequest.notificationType;

          if (!hasNotificationPhone || !hasNotificationType) {
            // Show missing fields dialog
            this.missingNotificationFieldsDialog = true;
            return;
          } else {
            // Show notification confirmation dialog
            this.notificationConfirmDialog = true;
            return;
          }
        }
      }

      this.saving = true;
      try {
        const data = {
          busChangeRequestID: this.selectedRequest.id,
          enteredBy: this.$store.state.authentication.user,
          startDate: this.formData.startDate
            ? moment(this.formData.startDate).format("MM/DD/YYYY")
            : null,
          endDate: this.formData.endDate
            ? moment(this.formData.endDate).format("MM/DD/YYYY")
            : null,
          startPeriod: this.formData.startPeriod,
          endPeriod: this.formData.endPeriod,
          note: this.formData.note,
          studentID: this.selectedRequest.studentID,
          homeAddress: this.selectedRequest.busAddress1 || "",
          busChangeAddress: this.formData.busChangeAddress,
          building: this.selectedRequest.building,
          branch: this.selectedRequest.branch,
          class: this.selectedRequest.class,
          section: this.selectedRequest.section,
          term: this.selectedRequest.regID,
          busChangePhoneNumber: this.formData.busChangePhoneNumber,
          geoPhoneNumber: this.formData.geoPhoneNumber,
          busChangeName: this.formData.busChangeName,
          busChangeAccountID: this.formData.busChangeAccountID,
          notificationPhoneNumber: this.selectedRequest
            ? this.selectedRequest.notificationPhoneNumber || ""
            : "",
          notificationType: this.selectedRequest
            ? this.selectedRequest.notificationType || ""
            : "",
          pickupRouteID: this.selectedPickupRoute
            ? this.selectedPickupRoute.id
            : null,
          dropoffRouteID: this.selectedDropoffRoute
            ? this.selectedDropoffRoute.id
            : null,
        };

        const response = await this.$http.post(
          "api/BusChangeRequests/convertToBusChange",
          data
        );

        if (response.error) {
          return this.$notify({
            group: "notification",
            type: "error",
            text: response.error,
          });
        }

        // Get the bus change ID from response
        const busChangeId = response.busChange ? response.busChange.id : null;

        this.$notify({
          text: "Bus change request completed successfully",
          group: "notification",
          type: "success",
        });

        // Send notification if needed
        if (this.savedBusChangeId && busChangeId) {
          await this.sendBusChangeNotification(busChangeId);
        }

        this.closeDialog();
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.$notify({
          text: "Failed to save bus change request",
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
        this.skipRouteWarning = false;
        this.skipNotification = false;
        this.savedBusChangeId = null;
      }
    },

    confirmSaveWithIncompleteRoute() {
      this.incompleteRouteDialog = false;
      this.skipRouteWarning = true;
      this.saveRequest();
    },

    getTeacherDisplay(student) {
      const teachers = [];
      if (student.teacherH) teachers.push(student.teacherH);
      if (student.teacherH2) teachers.push(student.teacherH2);
      return teachers.join(" / ") || "-";
    },

    onBusChangeSaved() {
      // Reload grid when bus change is saved
      this.$refs.grid.reloadGrid();
    },

    onViewDialogClose() {
      this.viewDialog = false;
      this.viewBusChangeId = null;
      this.viewDialogSendNotificationOnSave = false;
    },
  },
};
</script>

<style scoped>
.request-dialog .dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.request-dialog .dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: rgb(92, 184, 96);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8f5e9;
}

.recording-card {
  background-color: #fafafa;
}

.audio-player {
  width: 100%;
  min-height: 40px;
  display: block;
}

.compact-info {
  font-size: 0.8125rem;
}

.info-row {
  display: flex;
  padding: 4px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.75rem;
  color: #757575;
  min-width: 78px;
  padding-right: 6px;
  flex-shrink: 0;
}

.info-value {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #212121;
  flex: 1;
}

.info-value-full {
  margin-left: 0;
}

.info-row-audio {
  align-items: center;
}

.info-value-audio {
  display: flex;
  align-items: center;
}

.inline-audio-player {
  width: 100%;
  max-width: 230px;
  height: 30px;
}

.original-request-card {
  background: linear-gradient(135deg, #fff7f7 0%, #fff1f1 100%);
  border: 2px solid #ff9e9e !important;
  box-shadow: 0 3px 10px rgba(255, 107, 107, 0.16);
  border-radius: 4px;
}

.pending-request-indicator {
  font-size: 12px;
  font-weight: 600;
  color: #b71c1c;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 6px;
  padding: 6px 8px;
}

.save-btn {
  min-width: 140px;
}

.route-list-item-bus-change-request {
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 8px;
  transition: all 0.2s ease;
}

.route-list-item-bus-change-request:hover {
  background-color: rgba(92, 184, 96, 0.05);
}

.route-detail-chip {
  background-color: #e0e0e0;
  color: #000;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-left: 8px;
  display: inline-block;
}

.route-selection-wrapper {
  width: 100%;
}

.route-selection-main {
  display: flex;
  align-items: center;
}

.route-selection-driver {
  font-size: 0.75rem;
  color: #757575;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.comments-card {
  background: #fafafa;
  max-height: 460px;
  overflow-y: auto;
}

.comment-entry {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: white;
  text-align: left !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.comment-entry:last-child {
  margin-bottom: 0;
}

.comment-meta {
  font-size: 12px;
  color: #616161;
  margin-bottom: 4px;
  text-align: left !important;
  width: 100%;
}

.comment-caller {
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
  text-align: left !important;
  width: 100%;
}

.comment-text {
  font-size: 13px;
  white-space: normal;
  text-align: left !important;
  line-height: 1.4;
  width: 100%;
  margin-top: 8px;
  word-break: break-word;
}

.comment-audio {
  width: 100%;
  min-height: 36px;
}

.comment-awaiting {
  font-size: 11px;
  font-weight: 600;
  color: #b26a00;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 4px;
  padding: 2px 6px;
}

.period-helper-text {
  line-height: 1.35;
  padding-left: 2px;
}

.period-section-card {
  background: #fcfcfc;
  border-color: #e9e9e9 !important;
}

.period-title {
  color: #5f5f5f;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* Account Search Styles */
.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.search-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.search-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}

.account-list-container {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.account-list {
  background: transparent;
}

.account-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
}

.account-item:hover {
  background-color: #e8f5e9 !important;
}

.account-item:last-child {
  border-bottom: none;
}

.delete-warning-banner {
  border-radius: 8px;
}

.delete-summary-card {
  border: 1px solid #ffcdd2;
  background: #fff5f5;
  border-radius: 10px;
  padding: 10px 12px;
}

.delete-summary-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #c62828;
  margin-bottom: 8px;
}

.delete-summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
}

.delete-summary-item--full {
  grid-column: 1 / -1;
}

.delete-label {
  font-size: 11px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.delete-value {
  font-size: 13px;
  color: #212121;
  font-weight: 500;
  line-height: 1.35;
}
</style>
